<template>
  <div>
    <div class="bg-top shadow-sm"></div>
    <FormWizard
      v-if="startIndex !== null"
      ref="landownerReportWizard"
      id="notificationWizard"
      stepSize="xs"
      color="#008238"
      :startIndex="startIndex"
      @on-change="tabChanged"
    >
      <div slot="title">
        <div class="d-flex">
          <b-col lg="2" class="wizard-nav-item">
            <b-button
              v-if="adminEditing"
              variant="danger"
              pill
              class="py-2 w-100 w-lg-auto mr-2"
              @click="cancelAdminEdit"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              pill
              class="py-2 w-100 w-lg-auto"
              @click="saveButtonClicked"
              :disabled="!adminEditing && mfsAdmin && !startedByAdmin"
            >
              <template v-if="adminEditing">Save</template>
              <template v-else>Save<span class="d-none d-lg-inline-block">&nbsp;Draft</span></template>
            </b-button>
          </b-col>
          <b-col class="mobile-nav-title d-lg-none">
            <div>{{activeTabTitle}}</div>
          </b-col>
          <b-col lg="2" offset-lg="8" class="wizard-nav-item">
            <b-button-group class="pill-group">
              <b-button
                class="py-2 mr-2"
                variant="secondary"
                @click="back"
                :disabled="!activeTabIndex"
                aria-label="Go to Previous Step"
              >
                <span class="d-none d-lg-block">Back</span>
                <font-awesome-icon class="d-block d-lg-none" icon='caret-left' size="lg" />
              </b-button>
              <b-button
                class="py-2"
                variant="secondary"
                @click="next"
                :disabled="nextDisabled"
                aria-label="Go to Next Step"
              >
                <span class="d-none d-lg-block">Next</span>
                <font-awesome-icon class="d-block d-lg-none" icon='caret-right' size="lg" />
              </b-button>
            </b-button-group>
          </b-col>
        </div>
      </div>
      <template v-slot:step="props">
        <WizardStep
          :tab="updateTab(props.tab, props.index)"
          :transition="props.transition"
          :key="props.tab.title + disabledTabs[props.index]"
          :index="props.index"
          :title="disabledTabs[props.index] ? 'Not required' : ''"
          @click.native="stepClicked(props.index)"
          @keyup.native="e => stepKeyUp(e, props.index)"
        />
      </template>
      <TabContent title="Details" icon="icon-overview" :before-change="beforeTabChange">
        <GeneralDetailsForm :ref="formRefs[0]" :landownerReport="landownerReport" />
      </TabContent>
      <TabContent title="Involvement" icon="icon-contacts" :before-change="beforeTabChange">
        <InvolvementForm :ref="formRefs[1]" :landownerReport="landownerReport" />
      </TabContent>
      <TabContent title="Activities" icon="icon-details" :before-change="beforeTabChange">
        <ActivitiesForm :ref="formRefs[2]" :landownerReport="landownerReport" />
      </TabContent>
      <TabContent title="Stumpage" icon="icon-details" :before-change="beforeTabChange">
        <StumpageForm :ref="formRefs[3]" :landownerReport="landownerReport" />
      </TabContent>
      <TabContent title="Summary" icon="icon-review" :before-change="beforeTabChange">
        <Summary :ref="formRefs[4]" :landownerReport="landownerReport" />
      </TabContent>
      <div slot="footer"></div>
    </FormWizard>
    <SavingSpinner :show="saving"/>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { FormWizard, TabContent, WizardStep } from 'vue-form-wizard';
import 'vue-form-wizard/dist/vue-form-wizard.min.css';
import SavingSpinner from '@/components/common/SavingSpinner.vue';
import ActivitiesForm from '@/components/landownerreport/ActivitiesForm.vue';
import GeneralDetailsForm from '@/components/landownerreport/GeneralDetailsForm.vue';
import InvolvementForm from '@/components/landownerreport/InvolvementForm.vue';
import StumpageForm from '@/components/landownerreport/StumpageForm.vue';
import Summary from '@/components/landownerreport/summary/Summary.vue';

export default {
  name: 'LandownerReportForm',
  components: {
    FormWizard,
    ActivitiesForm,
    GeneralDetailsForm,
    InvolvementForm,
    SavingSpinner,
    StumpageForm,
    Summary,
    TabContent,
    WizardStep,
  },
  data() {
    return {
      saving: false,
      activeTabIndex: 0,
      activeTabTitle: '',
      startIndex: null,
      formRefs: [
        'generalDetailsForm',
        'involvementForm',
        'activitiesForm',
        'stumpageForm',
        'summaryForm',
      ],
    };
  },
  created() {
    this.setStartIndex();
  },
  computed: {
    ...mapState('LandownerReport', {
      landownerReport: state => state.landownerReport,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    stumpageFormRequired() {
      return this.landownerReport.harvest
        && [1, 2].indexOf(this.landownerReport.saleTypeId) !== -1
        && this.notification.ownershipAcresClassId !== 5;
    },
    disabledTabs() {
      const noActivities = !this.landownerReport.harvest && !this.landownerReport.nonHarvest;
      const involvementDisabled = noActivities;
      const activitiesDisabled = involvementDisabled
        || !this.landownerReport.involvementFormComplete;
      const stumpageDisabled = involvementDisabled
        || activitiesDisabled
        || !this.stumpageFormRequired;
      return [
        false,
        involvementDisabled,
        activitiesDisabled,
        stumpageDisabled,
        false,
      ];
    },
    nextTabIndex() {
      return this.disabledTabs
        .findIndex((disabled, index) => index > this.activeTabIndex && !disabled);
    },
    prevTabIndex() {
      const reversedIndex = this.disabledTabs
        .slice(0, this.activeTabIndex)
        .reverse()
        .indexOf(false);
      return this.activeTabIndex - 1 - reversedIndex;
    },
    nextDisabled() {
      return this.activeTabIndex === 4;
    },
    ...mapGetters('User', ['mfsAdmin']),
    ...mapGetters('LandownerReport', ['adminEditing']),
    startedByAdmin() {
      return this.landownerReport.statusId === 3;
    },
  },
  methods: {
    updateTab(propsTab, index) {
      const newTab = propsTab;
      newTab.checked = (propsTab.checked || this.activeTabIndex > index)
        && !this.disabledTabs[index];
      return newTab;
    },
    async next() {
      if (this.adminEditing) {
        this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, this.nextTabIndex);
      } else if (await this.saveDraft()) {
        this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, this.nextTabIndex);
      }
    },
    async back() {
      if (!this.adminEditing) {
        await this.saveDraft();
      }
      this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, this.prevTabIndex);
    },
    setActiveTabTitle() {
      if (this.$refs.landownerReportWizard.tabs[this.activeTabIndex]) {
        this.activeTabTitle = this.$refs.landownerReportWizard.tabs[this.activeTabIndex].title;
      }
    },
    tabChanged(prevIndex, nextIndex) {
      this.activeTabIndex = nextIndex;

      this.setActiveTabTitle();

      if (this.activeTabIndex === 4) {
        if (this.adminEditing) {
          this.syncFormDataToStore();
        }
        this.$refs[this.formRefs[4]].reload();
      }
      this.$scrollTo(this.$refs.landownerReportWizard, {
        container: document.querySelector('.wizard-tab-content'),
      });
    },
    setStartIndex() {
      const formComplete = [
        {
          complete: this.landownerReport.generalDetailsFormComplete,
          disabled: this.disabledTabs[0],
        },
        {
          complete: this.landownerReport.involvementFormComplete,
          disabled: this.disabledTabs[1],
        },
        {
          complete: this.landownerReport.activitiesFormComplete,
          disabled: this.disabledTabs[2],
        },
        {
          complete: this.landownerReport.stumpageFormComplete,
          disabled: this.disabledTabs[3],
        },
        {
          complete: false,
          disabled: this.disabledTabs[4],
        },
      ];

      this.startIndex = formComplete.map(fc => !fc.complete && !fc.disabled).indexOf(true);
      this.activeTabIndex = this.startIndex;

      // Always waiting on the DOM, I swear.
      this.$nextTick(() => {
        this.setActiveTabTitle();
      });
    },
    checkStepsUpTo(tabIndex) {
      for (let index = 0; index <= tabIndex; index += 1) {
        this.$refs.landownerReportWizard.tabs[index].checked = true;
      }
    },
    async stepKeyUp(event, stepIndex) {
      if (event.keyCode === 13) await this.stepClicked(stepIndex);
    },
    async stepClicked(stepIndex) {
      if (this.adminEditing) {
        if (!this.disabledTabs[stepIndex]) {
          this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, stepIndex);
        }
      } else {
        const saved = await this.saveDraft();
        if (!this.disabledTabs[stepIndex] && stepIndex <= this.activeTabIndex) {
          this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, stepIndex);
        } else if (saved && !this.disabledTabs[stepIndex]) {
          this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, stepIndex);
        }
      }
    },
    async saveButtonClicked() {
      if (this.adminEditing) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          'Are you sure you want to save all changes? The report will be marked as Complete - Edited.',
          {
            title: 'Save Changes',
            okTitle: 'Save',
            cancelTitle: 'Cancel',
            centered: true,
          },
        );
        if (confirmed) {
          await this.saveAllSteps();
        }
      } else {
        await this.saveDraft();
      }
    },
    async cancelAdminEdit() {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Are you sure you want to cancel? All changes will be discarded.',
        {
          title: 'Cancel Editing',
          okTitle: 'Yes, Cancel',
          cancelTitle: 'Continue Editing',
          okVariant: 'danger',
          centered: true,
        },
      );
      if (confirmed) {
        this.$store.dispatch('LandownerReport/stopAdminEdit');
        this.$router.go(0);
      }
    },
    async saveAllSteps() {
      this.saving = true;
      try {
        // Collect all form data upfront before any save triggers a store reload
        const formsData = [];
        for (let i = 0; i < this.formRefs.length; i += 1) {
          const formRef = this.formRefs[i];
          if (formRef && this.$refs[formRef] && !this.disabledTabs[i]) {
            formsData.push({ index: i, ref: this.$refs[formRef] });
          }
        }

        // Validate all forms first
        for (let i = 0; i < formsData.length; i += 1) {
          const { index, ref } = formsData[i];
          if (ref.$refs && ref.$refs.observer) {
            // eslint-disable-next-line no-await-in-loop
            const isValid = await ref.$refs.observer.validate();
            if (!isValid) {
              this.$refs.landownerReportWizard.changeTab(this.activeTabIndex, index);
              this.saving = false;
              return;
            }
          }
        }

        // Collect data from each form (mirroring each form's submitWrapper logic)
        const generalDetailsForm = this.$refs[this.formRefs[0]];
        const involvementForm = this.$refs[this.formRefs[1]];
        const activitiesForm = this.$refs[this.formRefs[2]];
        const stumpageForm = this.$refs[this.formRefs[3]];

        const generalDetailsData = generalDetailsForm
          ? { ...generalDetailsForm.generalDetails }
          : null;

        let involvementData = null;
        if (involvementForm) {
          involvementData = { ...involvementForm.model };
          if (!involvementData.operatorChanged) {
            involvementData.operatorAddress = null;
          }
          if (!involvementData.exported) {
            involvementData.exporters = [];
          }
        }

        let activitiesData = null;
        if (activitiesForm && !this.disabledTabs[2]) {
          activitiesData = { ...activitiesForm.model };
          if (!this.landownerReport.harvest) activitiesData.harvestActivities = null;
        }

        let stumpageData = null;
        if (stumpageForm && !this.disabledTabs[3]) {
          stumpageData = { ...stumpageForm.model };
        }

        // Save each step sequentially
        if (generalDetailsData) {
          // eslint-disable-next-line no-await-in-loop
          await this.$store.dispatch('LandownerReport/adminUpdateGeneralDetails', {
            data: generalDetailsData,
          });
        }
        if (involvementData && !this.disabledTabs[1]) {
          // eslint-disable-next-line no-await-in-loop
          await this.$store.dispatch('LandownerReport/adminUpdateInvolvement', {
            data: involvementData,
          });
        }
        if (activitiesData) {
          // eslint-disable-next-line no-await-in-loop
          await this.$store.dispatch('LandownerReport/adminUpdateActivities', {
            data: activitiesData,
          });
        }
        if (stumpageData) {
          // eslint-disable-next-line no-await-in-loop
          await this.$store.dispatch('LandownerReport/adminUpdateStumpages', {
            data: stumpageData,
          });
        }

        await this.$store.dispatch('LandownerReport/adminSubmitEdit', this.landownerReport.id);
        this.$router.go(0);
      } finally {
        this.saving = false;
      }
    },
    syncFormDataToStore() {
      const generalDetailsForm = this.$refs[this.formRefs[0]];
      const involvementForm = this.$refs[this.formRefs[1]];
      const activitiesForm = this.$refs[this.formRefs[2]];
      const stumpageForm = this.$refs[this.formRefs[3]];

      const data = {};
      if (generalDetailsForm) {
        Object.assign(data, generalDetailsForm.generalDetails);
      }
      if (involvementForm) {
        Object.assign(data, involvementForm.model);
      }
      if (activitiesForm) {
        const { harvestActivities, nonHarvestActivities, additionalInformation } = activitiesForm.model;
        Object.assign(data, harvestActivities, nonHarvestActivities, { additionalInformation });
      }
      if (stumpageForm && !this.disabledTabs[3]) {
        Object.assign(data, stumpageForm.model);
      }

      const editorComments = this.$store.state.LandownerReport.editorComments;
      data.editorCommentGeneralDetails = editorComments.generalDetails;
      data.editorCommentInvolvement = editorComments.involvement;
      data.editorCommentActivities = editorComments.activities;
      data.editorCommentStumpage = editorComments.stumpage;

      this.$store.commit('LandownerReport/UPDATE_LANDOWNER_REPORT_LOCAL', data);
    },
    beforeTabChange() {
      if (this.adminEditing) return true;
      return this.saveDraft();
    },
    async saveDraft() {
      if (this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) return true;

      this.saving = true;

      const formRef = this.formRefs[this.activeTabIndex];
      if (formRef) {
        const formResult = await this.$refs[formRef].submitWrapper();
        this.saving = false;
        return formResult && !this.tabsDisabled;
      }

      this.saving = false;
      return true;
    },
  },
};
</script>
<style>
.full-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.25);
  z-index: 10;
}

.above-full-mask {
  position: relative;
  z-index: 11;
}
</style>
